<template>
    <svg
        :width="width"
        :height="height"
        class="sparkline"
        :class="className"
    >
        <defs>
            <linearGradient
                :id="areaGradientId"
                x1="0"
                :y1="padding"
                x2="0"
                :y2="height - padding"
                gradientUnits="userSpaceOnUse"
            >
                <stop offset="0%" :stop-color="fillGradientTopColor" :stop-opacity="fillGradientTopOpacity" />
                <stop offset="100%" :stop-color="fillGradientBottomColor" :stop-opacity="fillGradientBottomOpacity" />
            </linearGradient>
        </defs>
        <path
            v-if="showArea && areaPath"
            :d="areaPath"
            :fill="`url(#${areaGradientId})`"
            stroke="none"
        />
        <path
            v-if="showLine && linePath"
            :d="linePath"
            fill="none"
            :stroke="lineColor"
            :stroke-width="lineWidth"
            stroke-linecap="round"
            stroke-linejoin="round"
        />
    </svg>
</template>

<script>
export default {
    name: 'SparklineChart',
    props: {
        data: {
            type: Array,
            required: true,
            default: () => []
        },
        width: {
            type: Number,
            default: 100
        },
        height: {
            type: Number,
            default: 30
        },
        className: {
            type: String,
            default: ''
        },
        smooth: {
            type: Boolean,
            default: true
        },
        tension: {
            type: Number,
            default: 0.45
        },
        showArea: {
            type: Boolean,
            default: true
        },
        showLine: {
            type: Boolean,
            default: true
        },
        lineColor: {
            type: String,
            default: '#1a1a1a'
        },
        lineWidth: {
            type: Number,
            default: 1.5
        },
        fillGradientTopColor: {
            type: String,
            default: '#1a1a1a'
        },
        fillGradientBottomColor: {
            type: String,
            default: '#f3f3f0'
        },
        fillGradientTopOpacity: {
            type: Number,
            default: 0.85
        },
        fillGradientBottomOpacity: {
            type: Number,
            default: 0.45
        },
        padding: {
            type: Number,
            default: 2
        }
    },
    computed: {
        areaGradientId() {
            return `sparkline-area-grad-${this._uid}`;
        },
        pointsXY() {
            if (!this.data || this.data.length === 0) {
                return [];
            }

            const values = this.data.map(d => typeof d === 'object' ? d.value : d);
            const min = Math.min(...values);
            const max = Math.max(...values);
            const range = max - min;

            const availableHeight = this.height - (this.padding * 2);
            const availableWidth = this.width - (this.padding * 2);
            const stepX = availableWidth / (values.length - 1 || 1);

            return values.map((value, index) => {
                const x = this.padding + (index * stepX);
                let y;
                
                if (range === 0) {
                    y = this.height / 2;
                } else {
                    const normalizedValue = (value - min) / range;
                    y = this.height - this.padding - (normalizedValue * availableHeight);
                }
                
                return { x, y };
            });
        },
        linePath() {
            if (this.pointsXY.length === 0) {
                return '';
            }

            if (this.pointsXY.length === 1) {
                const p = this.pointsXY[0];
                return `M ${p.x} ${p.y}`;
            }

            if (!this.smooth || this.pointsXY.length < 3) {
                return this.straightLinePath();
            }

            return this.smoothCurvePath();
        },
        areaPath() {
            if (!this.linePath) {
                return '';
            }

            const baselineY = this.height - this.padding;
            const firstX = this.pointsXY[0].x;
            const lastX = this.pointsXY[this.pointsXY.length - 1].x;

            return `${this.linePath} L ${lastX} ${baselineY} L ${firstX} ${baselineY} Z`;
        }
    },
    methods: {
        straightLinePath() {
            const points = this.pointsXY;
            let path = `M ${points[0].x} ${points[0].y}`;
            
            for (let i = 1; i < points.length; i++) {
                path += ` L ${points[i].x} ${points[i].y}`;
            }
            
            return path;
        },
        smoothCurvePath() {
            const points = this.pointsXY;
            let path = `M ${points[0].x} ${points[0].y}`;

            for (let i = 0; i < points.length - 1; i++) {
                const p0 = points[Math.max(0, i - 1)];
                const p1 = points[i];
                const p2 = points[i + 1];
                const p3 = points[Math.min(points.length - 1, i + 2)];

                const c1x = p1.x + (p2.x - p0.x) * (this.tension / 6);
                const c1y = p1.y + (p2.y - p0.y) * (this.tension / 6);
                const c2x = p2.x - (p3.x - p1.x) * (this.tension / 6);
                const c2y = p2.y - (p3.y - p1.y) * (this.tension / 6);

                path += ` C ${c1x} ${c1y}, ${c2x} ${c2y}, ${p2.x} ${p2.y}`;
            }

            return path;
        }
    }
};
</script>

<style scoped>
.sparkline {
    display: inline-block;
    vertical-align: middle;
}
</style>
